import { Lock, Sparkles } from "lucide-react";
import { BottomSheet } from "@/components/app/bottom-sheet";
import { TrendChart } from "@/components/pyq/trend-chart";
import { PRIORITY_BY_ID, TREND_LABEL, chapterPriority, trendDirection } from "@/lib/pyq/analytics";
import type { ChapterTrend } from "@/lib/pyq/types";
import { cn } from "@/lib/utils";

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-card/60 p-3">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-[15px] font-bold tabular-nums">{value}</p>
    </div>
  );
}

export function PyqChapterSheet({
  chapter,
  locked,
  onClose,
  onUpgrade,
}: {
  chapter: ChapterTrend | null;
  /** True when the chapter is premium and the viewer has no paid access. */
  locked: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}) {
  const priority = chapter ? PRIORITY_BY_ID.get(chapterPriority(chapter)) : undefined;

  return (
    <BottomSheet
      open={Boolean(chapter)}
      onClose={onClose}
      title={chapter?.chapter ?? ""}
      subtitle={chapter ? `Class ${chapter.classLevel}` : undefined}
      tall
    >
      {chapter ? (
        locked ? (
          <div className="rounded-3xl border border-white/10 bg-card/60 p-6 text-center">
            <span className="mx-auto grid size-12 place-items-center rounded-full bg-gradient-to-br from-violet-400/25 to-fuchsia-500/10 text-violet-500 dark:text-violet-300">
              <Lock className="size-5" />
            </span>
            <h3 className="mt-3 text-base font-bold">Premium chapter analysis</h3>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Unlock the full PYQ breakdown, subtopics and prediction note for this chapter.
            </p>
            <button
              type="button"
              onClick={onUpgrade}
              className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-500 px-5 py-3 text-[13px] font-bold text-primary-foreground shadow-[0_12px_30px_-12px_rgba(99,102,241,0.9)] ring-1 ring-inset ring-white/25 transition-transform active:scale-95"
            >
              <Sparkles className="size-4" />
              Unlock Premium
            </button>
          </div>
        ) : (
          <div className="space-y-4 pb-2">
            <div className="flex flex-wrap items-center gap-2">
              {priority ? (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold",
                    priority.className,
                  )}
                >
                  {priority.emoji} {priority.label}
                </span>
              ) : null}
              <span className="rounded-full border border-white/10 bg-surface px-2.5 py-1 text-[11px] font-semibold">
                {chapter.yieldCategory}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Stat label="Average questions" value={String(chapter.avgQuestions)} />
              <Stat label="Average marks" value={String(chapter.avgMarks)} />
              <Stat label="Repeat probability" value={chapter.repeatProbability} />
              <Stat label="Trend" value={TREND_LABEL[trendDirection(chapter.recentTrend)]} />
            </div>

            {chapter.recentTrend?.length ? (
              <div className="rounded-3xl border border-white/10 bg-card/60 p-4">
                <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  Last 5 years
                </p>
                <TrendChart values={chapter.recentTrend} showValues className="mt-3 h-24" />
              </div>
            ) : null}

            {chapter.subTopics?.length ? (
              <div className="rounded-3xl border border-white/10 bg-card/60 p-4">
                <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  Important subtopics
                </p>
                <ul className="mt-2 space-y-2">
                  {chapter.subTopics.map((s, i) => (
                    <li
                      key={`${s.name}-${i}`}
                      className="rounded-2xl border border-white/10 bg-surface/60 p-2.5 text-[13px]"
                    >
                      <span className="font-semibold">{s.name}</span>
                      <span className="mt-1 flex flex-wrap gap-1.5">
                        {s.probability ? (
                          <span className="rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2 py-0.5 text-[10.5px] font-bold text-emerald-600 dark:text-emerald-300">
                            {s.probability} chance
                          </span>
                        ) : null}
                        {s.primaryType ? (
                          <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
                            {s.primaryType}
                          </span>
                        ) : null}
                      </span>
                    </li>

                  ))}
                </ul>
              </div>
            ) : null}

            {chapter.predictionNote ? (
              <div className="rounded-3xl border border-cyan-400/25 bg-cyan-400/5 p-4">
                <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-cyan-600 dark:text-cyan-300">
                  Prediction note
                </p>
                <p className="mt-1.5 text-[13px] leading-relaxed">{chapter.predictionNote}</p>
              </div>
            ) : null}
          </div>
        )
      ) : null}
    </BottomSheet>
  );
}
